import { useIsBarclay, useLanguage } from '@/hooks/Contexts'
import { API_URL } from '@/utils/Consts'
import { Action0 } from '@/utils/Types'
import GooglePayBtn from '@google-pay/button-react'
import { appUtils } from 'lib/AppUtils'
import axios from 'lib/axios'
import { useRouter } from 'next/router'
import { CSSProperties, Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react'
import { FieldValues, UseFormReturn } from 'react-hook-form'
import { toast } from 'react-toastify'

export const GooglePayButton: FC<{
  total: number
  form: UseFormReturn<FieldValues, any>
  disabled: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
  cartId?: any
  toastNoAddress?: Action0
  extraCls?: string
  style?: CSSProperties
}> = ({ total, form, setLoading, disabled, cartId, toastNoAddress, extraCls, style }) => {
  const { errors: errStr, cart: cartStr, wallet: walletStr } = useLanguage()
  const router = useRouter()
  const isBarclays = useIsBarclay()
  const {
    getValues,
    setValue,
    handleSubmit,
    // trigger,
    formState,
  } = form
  const formInvalid = useMemo<boolean>(() => Object.keys(formState.errors)?.length > 0, [formState])
  const isCart = useMemo<boolean>(() => typeof cartId != 'undefined' && cartId != null, [cartId])

  const ref = useRef<GooglePayBtn['elementRef']>()

  const initStyle = useCallback(() => {
    const btn = ref.current.getElementsByTagName?.('button')?.[0]
    if (btn?.children?.length > 0) {
      for (let i = 0; i < btn.children.length; i++) {
        const element = btn.children[i]
        element?.setAttribute?.('style', 'display: none')
      }
      const text1 = document.createElement('span')
      text1.innerHTML = 'Pay with'

      const img = document.createElement('img')
      img.setAttribute('src', '/assets/google_pay.svg')
      img.setAttribute('class', 'w-6 h-6 ml-1 mr-0.5')

      const text2 = document.createElement('span')
      text2.innerHTML = 'Pay'

      const btnCls = '!bg-transparent flex !min-w-fit items-center justify-center whitespace-nowrap !rounded-full !outline-none'
      btn.classList.add(...btnCls.split(' '))
      btn.append(text1, img, text2)
      // btn.toggleAttribute('disabled', disabled)
    }
  }, [ref.current])

  // init button style
  useEffect(() => {
    let interval = null
    if (ref.current?.getElementsByTagName?.('button')?.[0]) {
      initStyle()
    } else {
      interval = setInterval(() => {
        if (ref.current?.getElementsByTagName?.('button')?.[0]) {
          initStyle()
          clearInterval(interval)
        }
      }, 100)
    }
    return () => {
      clearInterval(interval)
    }
  }, [])
  // change button style
  useEffect(() => {
    const btn = ref.current?.getElementsByTagName?.('button')?.[0]
    if (btn) {
      btn.toggleAttribute('disabled', disabled || formInvalid) // disable btn func
      btn.setAttribute('aria-grey', disabled) // change btn style
      btn.setAttribute('aria-disabled', formInvalid) // won't affect btn style
    }
  }, [ref.current, disabled, formInvalid])

  // eslint-disable-next-line no-unused-vars
  const onClick: (event: Event) => void = event => {
    // trigger(undefined, { shouldFocus: true }).then(valid => {
    if (disabled || formInvalid) {
      event?.preventDefault?.()
      event?.stopImmediatePropagation?.()
      return
    } else if (typeof toastNoAddress == 'function' && !getValues('address')) {
      toastNoAddress()
      event?.preventDefault?.()
      return
    } else {
      // proceed to google pay button click event
      setLoading(true)
    }
    // })
  }

  // eslint-disable-next-line no-unused-vars
  const onPaymentAuthorized: google.payments.api.PaymentAuthorizedHandler = paymentData => {
    // useStats
    // gateway barclay token + btoa
    // gateway checkout tokenizationData
    const token = isBarclays ? paymentData?.paymentMethodData?.tokenizationData?.token : paymentData?.paymentMethodData?.tokenizationData
    if (token) {
      setValue('google_auth', isBarclays ? btoa(token as string) : token, { shouldDirty: true })
      return { transactionState: 'SUCCESS' }
    } else {
      setValue('google_auth', null, { shouldDirty: true })
      return {
        transactionState: 'ERROR',
        error: { reason: 'PAYMENT_DATA_INVALID', intent: 'PAYMENT_AUTHORIZATION', message: 'Payment authorization failed' },
      }
    }
  }

  // eslint-disable-next-line no-unused-vars
  const onLoadPaymentData: (paymentData: google.payments.api.PaymentData) => void = paymentData => {
    if (getValues('google_auth')) {
      handleSubmit(data => {
        setLoading(true)
        const payload = {
          address: data?.address,
          payment: {
            gateway: isBarclays ? 'barclaycard_google_pay' : 'checkout_google_pay', // add checkout
            google_auth: data?.google_auth,
            amount: total,
            currency: 'GBP',
          },
        }
        if (isCart) {
          payload['cart_id'] = cartId
        } else {
          payload['amount'] = total
          payload['currency'] = 'GBP'
        }
        let error = null
        axios
          .post(isCart ? API_URL.checkout : API_URL.topUp, payload)
          .then(res => {
            const url = res?.data?.code == 0 && (res?.data?.data?.url || (res?.data?.data?.id ? '/cart/' + res.data.data.id : null))
            if (url) {
              router.push(url)
              toast.success(isCart ? cartStr.success : walletStr.top_up_success, { toastId: 'google-pay' })
            } else {
              error = { response: res }
            }
          })
          .catch(err => (error = err))
          .finally(() => {
            setLoading(false)
            if (error) {
              toast.error(appUtils.handleApiError(error, errStr), { toastId: 'google-pay' })
            }
          })
      })()
    }
  }

  return (
    <GooglePayBtn
      ref={x => (ref.current = x?.['elementRef']?.current)} // to change button style
      environment='PRODUCTION'
      paymentRequest={{
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
          {
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks: ['VISA', 'MASTERCARD'],
              assuranceDetailsRequired: true,
            },
            tokenizationSpecification: {
              type: 'PAYMENT_GATEWAY',
              parameters: isBarclays
                ? {
                    gateway: process.env.NEXT_PUBLIC_GOOGLE_PAY_GATEWAY_BARCLAYS,
                    gatewayMerchantId: process.env.NEXT_PUBLIC_GOOGLE_PAY_GATEWAY_BARCLAYS_MERCHANT_ID,
                  }
                : {
                    gateway: process.env.NEXT_PUBLIC_GOOGLE_PAY_GATEWAY_CHECKOUT,
                    gatewayMerchantId: process.env.NEXT_PUBLIC_GOOGLE_PAY_GATEWAY_CHECKOUT_MERCHANT_ID,
                  },
            },
          },
        ],
        merchantInfo: {
          merchantId: process.env.NEXT_PUBLIC_GOOGLE_PAY_MERCHANT_ID,
          merchantName: process.env.NEXT_PUBLIC_GOOGLE_PAY_MERCHANT_NAME,
        },
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPriceLabel: 'Total',
          totalPrice: String(total),
          currencyCode: 'GBP',
          countryCode: 'GB',
        },
        callbackIntents: ['PAYMENT_AUTHORIZATION'],
      }}
      onPaymentAuthorized={onPaymentAuthorized}
      onLoadPaymentData={onLoadPaymentData}
      onClick={onClick}
      onCancel={reason => {
        toast.warn(reason?.statusMessage || reason?.statusCode || 'Payment canceled', { toastId: 'google-pay' })
        setLoading(false)
      }}
      onError={error => {
        toast.warn((error instanceof Error ? error.message : error.statusMessage) || 'Payment error', { toastId: 'google-pay' })
        setLoading(false)
      }}
      existingPaymentMethodRequired={false} // display when user doesn't have existing pay method
      buttonColor='default'
      buttonType='pay'
      buttonLocale='en'
      buttonSizeMode='fill'
      className={`transition-opacity ease-in ${extraCls}`}
      style={style} // style for container div
    />
  )
}
